import type { TFunction } from "i18next";
import type { AdminNotification } from "@/services/adminNotificationService";
import { CATEGORY_BADGE } from "./notificationBadgeConfig";
import { notificationMetaString } from "./notificationMetadata";

const UNRESOLVED_I18N_PLACEHOLDER = /\{\{[^}]+\}\}/;

function metaOrEmpty(
  metadata: Record<string, unknown> | null,
  key: string,
): string {
  return notificationMetaString(metadata, key) ?? "";
}

/**
 * Interpolation values without the blank ones: i18next renders an explicit ""
 * as empty text, while an omitted key stays `{{placeholder}}` and lets the
 * caller detect the gap and fall back to the stored title.
 */
function presentValues(values: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value));
}

function formatDeviceLowBatteryTitle(
  t: TFunction<"adminNotifications">,
  notification: AdminNotification,
): string | null {
  const meta = notification.metadata_json;
  const kind = metaOrEmpty(meta, "kind");
  const houseName = metaOrEmpty(meta, "house_name");
  // Readers: only explicit kiosk_name (never fall back to serial in display_label).
  const kioskName =
    kind === "reader"
      ? metaOrEmpty(meta, "kiosk_name")
      : metaOrEmpty(meta, "kiosk_name") || metaOrEmpty(meta, "display_label");

  let key: string;
  if (kind === "reader") {
    if (houseName && kioskName) {
      key = "titles.device.low_battery_reader_at_house";
    } else if (kioskName) {
      key = "titles.device.low_battery_reader_linked";
    } else if (houseName) {
      key = "titles.device.low_battery_reader_at_house_only";
    } else {
      key = "titles.device.low_battery_reader";
    }
  } else if (kind === "kiosk" || kioskName) {
    key = houseName
      ? "titles.device.low_battery_kiosk_at_house"
      : "titles.device.low_battery_kiosk";
  } else {
    return null;
  }

  const translated = t(key, {
    ...presentValues({ kioskName, houseName }),
    defaultValue: "",
  });
  if (translated && !UNRESOLVED_I18N_PLACEHOLDER.test(translated)) {
    return translated;
  }
  return null;
}

/** Maps API `event_type` to nested `titles.*` keys; falls back to stored title. */
export function formatNotificationTitle(
  t: TFunction<"adminNotifications">,
  notification: AdminNotification,
): string {
  if (notification.event_type === "device.low_battery") {
    const deviceTitle = formatDeviceLowBatteryTitle(t, notification);
    if (deviceTitle) {
      return deviceTitle;
    }
  }

  const batteryLevelRaw = notification.metadata_json?.battery_level;
  const batteryLevel =
    typeof batteryLevelRaw === "number"
      ? String(batteryLevelRaw)
      : metaOrEmpty(notification.metadata_json, "battery_level");
  const translated = t(`titles.${notification.event_type}`, {
    ...presentValues({
      ticketNumber: metaOrEmpty(notification.metadata_json, "ticket_number"),
      activityName: metaOrEmpty(notification.metadata_json, "activity_name"),
      customerName: metaOrEmpty(notification.metadata_json, "customer_name"),
      displayLabel: metaOrEmpty(notification.metadata_json, "display_label"),
      batteryLevel,
    }),
    defaultValue: "",
  });
  if (translated && !UNRESOLVED_I18N_PLACEHOLDER.test(translated)) {
    return translated;
  }
  return notification.title;
}

export function formatNotificationCategory(
  t: TFunction<"adminNotifications">,
  category: string,
): string {
  if (category in CATEGORY_BADGE) {
    return t(`category.${category}`);
  }
  return t("category.unknown", { value: category });
}
